import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Me, Employee, Lang, MonthStatus, RetailMonth } from "../lib/types";
import { getMe, getEmployees, getRetailMonth, patchLang as apiPatchLang } from "../lib/api";
import { t as tFn } from "../lib/i18n";
import { useAvailableMonths } from "../lib/useAvailableMonths";

interface AuthState {
  me: Me | null;
  error: string | null;
  maintenanceMsg: { ru: string; uk: string };
  employees: Employee[];          // только у админа; сотрудница список коллег не получает
  reloadEmployees: () => Promise<void>;
  setLang: (l: Lang) => void;
  lang: Lang;
  t: (key: string, vars?: Record<string, string | number>) => string;
  showAdmin: boolean;
  setShowAdmin: (v: boolean) => void;
  adminInitialTab: string | null;
  setAdminInitialTab: (tab: string | null) => void;
  setMaintenanceMode: (on: boolean) => void;
  availableMonths: string[];
  monthStatuses: MonthStatus[];
  // Главный экран (этап 3)
  month: string;                  // выбранный месяц "YYYY-MM" или "" пока месяцев нет
  setMonth: (m: string) => void;
  view: string;                   // владелец: 'all' | 'store:<key>' | 'p:<personId>'; сотрудница: всегда 'all' (видит только себя)
  setView: (v: string) => void;
  retail: RetailMonth | null;     // данные выбранного месяца
  retailLoading: boolean;
  retailError: string | null;     // 'NO_DATA' или текст ошибки
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [maintenanceMsg, setMaintenanceMsg] = useState<{ ru: string; uk: string }>({ ru: "", uk: "" });
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [lang, setLangState] = useState<Lang>("ru");
  const [showAdmin, setShowAdmin] = useState<boolean>(false);
  const [adminInitialTab, setAdminInitialTab] = useState<string | null>(null);
  const { months: availableMonths, statuses: monthStatuses } = useAvailableMonths();
  const [month, setMonth] = useState<string>("");
  const [view, setView] = useState<string>("all");
  const [retail, setRetail] = useState<RetailMonth | null>(null);
  const [retailLoading, setRetailLoading] = useState(false);
  const [retailError, setRetailError] = useState<string | null>(null);

  async function reloadEmployees() {
    try {
      const { employees } = await getEmployees();
      setEmployees(employees);
    } catch { /* non-fatal */ }
  }

  useEffect(() => {
    getMe()
      .then(async (m) => {
        setMe(m);
        setLangState(m.lang);
        if (m.is_admin) await reloadEmployees();
      })
      .catch((e: any) => {
        if (e.message === "MAINTENANCE") {
          setError("MAINTENANCE");
          setMaintenanceMsg({
            ru: e.maintenance?.message_ru ?? "",
            uk: e.maintenance?.message_uk ?? "",
          });
          return;
        }
        setError(e.message);
      });
  }, []);

  // Месяц по умолчанию — последний с данными; если выбранный исчез из списка — тоже последний.
  useEffect(() => {
    if (availableMonths.length === 0) { setMonth(""); return; }
    if (!availableMonths.includes(month)) setMonth(availableMonths[availableMonths.length - 1]!);
  }, [availableMonths, month]);

  // Данные месяца. Сотруднице API отдаёт только её строки, админу — все.
  useEffect(() => {
    if (!me || !month) { setRetail(null); setRetailError(null); return; }
    let cancelled = false;
    setRetailLoading(true);
    setRetailError(null);
    getRetailMonth(month)
      .then(r => { if (!cancelled) setRetail(r); })
      .catch((e: any) => { if (!cancelled) { setRetail(null); setRetailError(e.message || "ERROR"); } })
      .finally(() => { if (!cancelled) setRetailLoading(false); });
    return () => { cancelled = true; };
  }, [me, month]);

  const setLang = (l: Lang) => {
    setLangState(l);
    apiPatchLang(l).catch(() => { /* non-fatal */ });
  };

  function setMaintenanceMode(on: boolean) {
    setMe(prev => prev ? { ...prev, maintenance_mode: on } : prev);
  }

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === "visible") {
        getMe().then(setMe).catch(() => { /* swallow */ });
      }
    }
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const value: AuthState = useMemo(() => ({
    me, error, maintenanceMsg, employees, reloadEmployees,
    setLang, lang,
    t: (key, vars) => tFn(key, lang, vars),
    showAdmin, setShowAdmin,
    adminInitialTab, setAdminInitialTab,
    setMaintenanceMode,
    availableMonths, monthStatuses,
    month, setMonth, view, setView, retail, retailLoading, retailError,
  }), [me, error, maintenanceMsg, employees, lang, showAdmin, adminInitialTab, availableMonths, monthStatuses, month, view, retail, retailLoading, retailError]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
